import React, { useCallback } from 'react';
import type { DocNode as ADF } from '@atlaskit/adf-schema';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	MODAL,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { FlagWithCommentType } from '../../../model/modals/types';
import type { CardId } from '../../../model/types';
import { useBacklogSelector, useBacklogDispatch } from '../../../state';
import { cardsFlagWithCommentRequest } from '../../../state/actions/cards/flag-with-comment';
import { closeModal } from '../../../state/actions/modals';
import { getOpenModalData } from '../../../state/selectors/modals';
import { FlagWithCommentRenderer } from './view';

const useFlagWithCommentState = () => ({
	modalData: useBacklogSelector(getOpenModalData),
});

const useFlagWithCommentActions = () => {
	const dispatch = useBacklogDispatch();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	/*
      Should only need to create this once since the modal is closed
      by either confirm or cancel
    */
	const modalAnalyticsEvent = createAnalyticsEvent({
		actionSubject: 'button',
		action: 'clicked',
	});

	const onClose = useCallback(() => {
		fireUIAnalytics(modalAnalyticsEvent, 'cancelButton');
		dispatch(closeModal());
	}, [dispatch, modalAnalyticsEvent]);

	const onFlagWithComment = useCallback(
		(cards: CardId[], flag: boolean, comment: ADF) => {
			fireUIAnalytics(modalAnalyticsEvent, 'confirmButton');
			const requestAnalyticsEvent = createAnalyticsEvent({
				actionSubject: 'cards',
				action: 'flagged',
			});
			dispatch(cardsFlagWithCommentRequest({ cards, flag, comment }, requestAnalyticsEvent));
			dispatch(closeModal());
		},
		[dispatch, modalAnalyticsEvent, createAnalyticsEvent],
	);

	return {
		onClose,
		onFlagWithComment,
	};
};

const FlagWithComment = () => {
	const { modalData } = useFlagWithCommentState();
	const { onClose, onFlagWithComment } = useFlagWithCommentActions();
	if (modalData?.type !== FlagWithCommentType) {
		return null;
	}
	const { flagAction, cards } = modalData.payload;
	return (
		<FlagWithCommentRenderer
			flagAction={flagAction}
			cards={cards}
			onClose={onClose}
			onFlagWithComment={onFlagWithComment}
		/>
	);
};

export default function FlagWithCommentWithAnalytics() {
	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="flagCardsWithCommentBacklog">
			<FlagWithComment />
		</ContextualAnalyticsData>
	);
}
